import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Home, Package, Lightbulb, Sparkles, Sun, Moon, Menu, X, Users } from 'lucide-react';
import ComingSoonModal from './ComingSoonModal';
import '../styles/navbar.css';

export default function Navbar() {
  const { theme, toggleTheme, themes } = useTheme();
  const { user } = useAuth();  // Get auth state
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 100) {
        setVisible(true);
      } else if (currentScrollY > lastScrollY) {
        setVisible(false);
      } else {
        setVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigation items - removed Dashboard to avoid duplication with button
  const navItems = [
    { name: 'Home', link: '/', icon: <Home size={20} />, isRoute: true },
    { name: 'Features', link: '/#features', icon: <Sparkles size={20} />, isRoute: true },
    { name: 'Packages', link: '/#packages', icon: <Package size={20} />, isRoute: true },
    { name: 'How It Works', link: '/#how-it-works', icon: <Lightbulb size={20} />, isRoute: true },
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className={`floating-navbar ${visible ? 'visible' : 'hidden'}`}>
        <div className="floating-navbar-container" style={isMobile ? { justifyContent: 'space-between', width: '100%', gap: '0' } : {}}>
          <Link to="/" className="navbar-logo">
            <picture>
              <source srcSet="/TechGenieMascot.webp" type="image/webp" />
              <img src="/TechGenieMascot.png" alt="TeachGenie™.ai" className="navbar-logo-img" />
            </picture>
            <span className="navbar-logo-text">
              <span className="teach-text">Teach</span>
              <span className="genie-text">Genie</span>
              <span className="tm-text">™</span>
            </span>
          </Link>

          {/* Desktop Menu */}
          <ul className="floating-nav-menu">
            {navItems.map((item, index) => (
              <li key={index}>
                {item.isComingSoon ? (
                  <button
                    onClick={() => setShowComingSoonModal(true)}
                    className="nav-item"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-text">{item.name}</span>
                  </button>
                ) : item.isRoute ? (
                  <Link to={item.link} className="nav-item">
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-text">{item.name}</span>
                  </Link>
                ) : (
                  <a href={item.link} className="nav-item">
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-text">{item.name}</span>
                  </a>
                )}
              </li>
            ))}
          </ul>

          <div className="nav-actions" style={isMobile ? { marginLeft: 'auto' } : {}}>
            {!isMobile && (
              <Link to="/lpu" className="lpu-logo-link hide-mobile">
                <img src="/lpu logo.png" alt="LPU" className="lpu-nav-logo" />
              </Link>
            )}
            <Link to={user ? '/generator' : '/login'} className="nav-cta-btn">
              {user ? (
                <div className="nav-cta-content">
                  <span className="cta-small">Member</span>
                  <span className="cta-big">Go create</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.1' }}>
                  <span style={{ fontSize: '0.55rem', textTransform: 'none', opacity: 0.9, fontWeight: 600 }}>Member?</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 900 }}>Login</span>
                </div>
              )}
            </Link>

            {/* Mobile Hamburger */}
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-content">
          <ul className="mobile-nav-list">
            {navItems.map((item, index) => (
              <li key={index}>
                {item.isComingSoon ? (
                  <button
                    onClick={() => {
                      setShowComingSoonModal(true);
                      handleNavClick();
                    }}
                    className="mobile-nav-item"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                  >
                    <span className="mobile-nav-icon">{item.icon}</span>
                    <span className="mobile-nav-text">{item.name}</span>
                  </button>
                ) : item.isRoute ? (
                  <Link to={item.link} className="mobile-nav-item" onClick={handleNavClick}>
                    <span className="mobile-nav-icon">{item.icon}</span>
                    <span className="mobile-nav-text">{item.name}</span>
                  </Link>
                ) : (
                  <a href={item.link} className="mobile-nav-item" onClick={handleNavClick}>
                    <span className="mobile-nav-icon">{item.icon}</span>
                    <span className="mobile-nav-text">{item.name}</span>
                  </a>
                )}
              </li>
            ))}
          </ul>
            <Link to={user ? '/generator' : '/login'} className="mobile-cta-btn" onClick={handleNavClick}>
              {user ? (
                <div className="nav-cta-content">
                  <span className="cta-small">Member</span>
                  <span className="cta-big">Go create</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.1' }}>
                  <span style={{ fontSize: '0.55rem', textTransform: 'none', opacity: 0.9, fontWeight: 600 }}>Member?</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 900 }}>Login</span>
                </div>
              )}
            </Link>

            {/* LPU Faculty Special Link */}
            <Link to="/lpu" className="mobile-nav-item lpu-faculty-drawer-link" onClick={handleNavClick}>
              <span className="mobile-nav-icon">
                <img src="/lpu logo.png" alt="LPU" style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
              </span>
              <span className="mobile-nav-text">LPU Faculty</span>
            </Link>
          </div>
      </div>

      {/* Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        featureName="Dashboard"
      />
    </>
  );
}

